import type { MediaSourceData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { BaseRegistry, type ItemRepresentation } from './BaseRegistry';

import type { AssetContainerModel, MediaCandidates } from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    SlideBackgroundModelTypeEnum,
    SourceEnum,
} from 'js/src/client';
import { convertBackgroundAssetToFlattenedAsset } from 'js/src/data/media';
import { INVISIBLE_IMAGE_SOURCE_DATA } from 'js/src/libs/consts';

interface MediaRepresentation extends ItemRepresentation {
    type: 'image' | 'video';
    attractiveness: number | 'UNKNOWN';
    sourceCategory: 'source' | 'uploads' | 'stock';
}

export class MediaRegistry extends BaseRegistry<MediaRepresentation> {
    private static instance: MediaRegistry;

    public static getInstance(): MediaRegistry {
        if (!MediaRegistry.instance) {
            MediaRegistry.instance = new MediaRegistry();
        }
        return MediaRegistry.instance;
    }

    protected validateItemData(data: AssetContainerModel): void {
        if (!data.background || (!data.background.image && !data.background.video)) {
            throw new Error('Invalid media data: must contain either image or video background');
        }
    }

    protected extractItemInfo(data: AssetContainerModel): {
        title: string;
        description: string;
        tags: string[];
    } {
        // Extract title
        let title = 'Untitled';
        if (data.background?.image?.title) {
            title = data.background.image.title;
        } else if (data.background?.video?.title) {
            title = data.background.video.title;
        }

        // Extract description
        let description = '';
        if (data.background?.image?.description) {
            description = data.background.image.description;
        } else if (data.background?.video?.description) {
            description = data.background.video.description;
        }

        // Extract tags
        let tags: string[] = [];
        if (data.background?.image?.tags) {
            tags = Array.isArray(data.background.image.tags) ? data.background.image.tags : [];
        } else if (data.background?.video?.tags) {
            tags = Array.isArray(data.background.video.tags) ? data.background.video.tags : [];
        }

        return { title, description, tags };
    }

    protected createRepresentation(
        id: string,
        itemInfo: { title: string; description: string; tags: string[] },
        data: AssetContainerModel,
    ): MediaRepresentation {
        // Determine type based on data
        const type: 'image' | 'video' = data.background?.video ? 'video' : 'image';

        const suitability_score =
            data.background?.video?.suitability_score ?? data.background?.image?.suitability_score;

        // Categorize source
        const source = data.background?.video?.source ?? data.background?.image?.source;
        let sourceCategory: 'source' | 'uploads' | 'stock' = 'stock';
        if (
            source === SourceEnum.SOURCE_ARTICLE ||
            source === SourceEnum.SOURCE_DOCUMENT ||
            source === SourceEnum.SOURCE_WEB_SEARCH
        ) {
            sourceCategory = 'source';
        } else if (source === SourceEnum.SOURCE_USER || source === SourceEnum.SOURCE_USER_OUTRO) {
            sourceCategory = 'uploads';
        }

        return {
            id,
            type,
            title: itemInfo.title,
            description: itemInfo.description,
            tags: itemInfo.tags,
            attractiveness: suitability_score ?? 'UNKNOWN',
            sourceCategory,
        };
    }

    protected getItemUuid(data: AssetContainerModel): string | undefined {
        return (data.background?.image?.uuid || data.background?.video?.uuid) ?? undefined;
    }

    protected isItemAlreadyInRegistry(uuid: string): string | null {
        for (const id of this.registry.keys()) {
            const entry = this.registry.get(id);
            if (!entry) {
                continue;
            }
            if (
                entry.data.background?.video?.uuid === uuid ||
                entry.data.background?.image?.uuid === uuid
            ) {
                return id;
            }
        }
        return null;
    }

    private needsEnriching(data: AssetContainerModel): boolean {
        if (data.background?.image) {
            return (
                data.background.image.source === SourceEnum.SOURCE_ARTICLE ||
                data.background.image.source === SourceEnum.SOURCE_USER
            );
        }
        if (data.background?.video) {
            return (
                data.background.video.source === SourceEnum.SOURCE_ARTICLE ||
                data.background.video.source === SourceEnum.SOURCE_USER ||
                data.background.video.source === SourceEnum.SOURCE_USER_OUTRO ||
                data.background.video.source === SourceEnum.SOURCE_DOCUMENT
            );
        }
        return false;
    }

    private async fetchAIDescription(uuid: string): Promise<string | null> {
        try {
            const response = await ApiService.mediaAssetTextRepresentationRetrieve({
                path: { media_asset_uuid: uuid },
            });
            return response.data.text_representation || null;
        } catch (error) {
            // If AI description is not available, return null to fall back to basic info
            return null;
        }
    }

    public getMediaSourceData(id: string): MediaSourceData | null {
        const entry = this.registry.get(id);
        if (!entry) {
            return null;
        }

        // Handle background assets (image/video)
        if (entry.data.background) {
            return convertBackgroundAssetToFlattenedAsset(entry.data.background) as MediaSourceData;
        }

        return null;
    }

    /**
     * Checks if a media asset is an invisible/transparent image (no visible content).
     * Returns an error message if the media is invisible, or null if it's valid.
     */
    public validateNotInvisibleImage(id: string): string | null {
        const mediaData = this.getItem(id);
        if (!mediaData) {
            return `Media with ID "${id}" not found in registry.`;
        }

        // Check if the media is the invisible asset
        if (
            mediaData.type === AssetContainerTypes.BACKGROUND &&
            mediaData.background?.type === SlideBackgroundModelTypeEnum.IMAGE
        ) {
            const sourceMatch =
                mediaData.background?.image?.source === INVISIBLE_IMAGE_SOURCE_DATA.source;
            const sourceIdMatch =
                mediaData.background?.image?.source_id === INVISIBLE_IMAGE_SOURCE_DATA.source_id;
            if (sourceMatch && sourceIdMatch) {
                return `Media with ID "${id}" is an invisible/transparent image which means no media is visible (effectively unset).`;
            }
        }

        return null;
    }

    public async addAnyUnseenItemsFromVideo(luminary: Luminary): Promise<void> {
        // Go through all scenes and extract media assets (not icons)
        const unseenUuids = new Set<string>();
        for (const scene of luminary.scenes) {
            // filter out already seen media assets
            const mediaAssets = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
            for (const asset of mediaAssets) {
                const uuid = asset.sourceData.uuid;
                if (uuid && !this.getItemIdByUuid(uuid)) {
                    unseenUuids.add(uuid);
                }
            }
        }

        const assets = await this.fetchItemsFromApi(unseenUuids);

        for (const asset of assets) {
            if (asset.background) {
                this.addItem(asset);
            }
        }
    }

    /**
     * Enhanced version of fetchItemsFromApi that also fetches AI descriptions for source media
     */
    protected override async fetchItemsFromApi(
        unseenUuids: Set<string>,
    ): Promise<AssetContainerModel[]> {
        // First get the basic asset data
        const assets = await super.fetchItemsFromApi(unseenUuids);

        // For source media, fetch AI descriptions and enhance the data
        const enhancedAssets = await Promise.all(
            assets.map(async (asset) => {
                if (this.needsEnriching(asset)) {
                    const uuid = this.getItemUuid(asset);
                    if (uuid) {
                        const aiDescription = await this.fetchAIDescription(uuid);
                        if (aiDescription) {
                            // Clone the asset and enhance with AI description
                            const enhancedAsset = { ...asset };
                            if (enhancedAsset.background?.image) {
                                const currentTitle = enhancedAsset.background.image.title;
                                const shouldPreserveTitle =
                                    currentTitle &&
                                    currentTitle !== 'Untitled' &&
                                    currentTitle.trim().length > 0;

                                enhancedAsset.background = {
                                    ...enhancedAsset.background,
                                    image: {
                                        ...enhancedAsset.background.image,
                                        description: aiDescription,
                                        title: shouldPreserveTitle
                                            ? `${currentTitle} - ${aiDescription}`
                                            : aiDescription,
                                    },
                                };
                            } else if (enhancedAsset.background?.video) {
                                const currentTitle = enhancedAsset.background.video.title;
                                const shouldPreserveTitle =
                                    currentTitle &&
                                    currentTitle !== 'Untitled' &&
                                    currentTitle.trim().length > 0;

                                enhancedAsset.background = {
                                    ...enhancedAsset.background,
                                    video: {
                                        ...enhancedAsset.background.video,
                                        description: aiDescription,
                                        title: shouldPreserveTitle
                                            ? `${currentTitle} - ${aiDescription}`
                                            : aiDescription,
                                    },
                                };
                            }
                            return enhancedAsset;
                        }
                    }
                }
                return asset;
            }),
        );

        return enhancedAssets;
    }

    /**
     * Gets all UUIDs for source media in the registry.
     * This is useful for identifying media that originated from articles or documents.
     */
    public getSourceMediaUuids(): string[] {
        const sourceUuids: string[] = [];
        const sourcesToCheck = [
            SourceEnum.SOURCE_ARTICLE,
            SourceEnum.SOURCE_DOCUMENT,
            SourceEnum.SOURCE_WEB_SEARCH,
            SourceEnum.SOURCE_USER, // currently uploads in Sketch are uploaded as user uploads
        ];

        for (const entry of this.registry.values()) {
            const data = entry.data;

            // Check if this is source media (from article or document)
            const imageSource = data.background?.image?.source;
            const videoSource = data.background?.video?.source;
            const isSourceMedia =
                (imageSource !== undefined && sourcesToCheck.includes(imageSource)) ||
                (videoSource !== undefined && sourcesToCheck.includes(videoSource));

            if (isSourceMedia) {
                const uuid = this.getItemUuid(data);
                if (uuid) {
                    sourceUuids.push(uuid);
                }
            }
        }

        return sourceUuids;
    }

    /**
     * Prepopulates the MediaRegistry with media candidates from video recommendations.
     * This allows the Improve Media Agent to start with a curated set of media assets
     * instead of having to search from scratch.
     */
    public async addCandidatesFromRecommendations(
        mediaCandidates: MediaCandidates,
    ): Promise<number> {
        const allUuids = new Set<string>();

        // Collect all UUIDs from different media candidate arrays
        if (mediaCandidates.source_media) {
            mediaCandidates.source_media.forEach((uuid) => allUuids.add(uuid));
        }
        if (mediaCandidates.uploaded_media) {
            mediaCandidates.uploaded_media.forEach((uuid) => allUuids.add(uuid));
        }
        if (mediaCandidates.stock_media) {
            mediaCandidates.stock_media.forEach((uuid) => allUuids.add(uuid));
        }

        // Filter out UUIDs that are already in the registry
        const unseenUuids = new Set<string>();
        for (const uuid of allUuids) {
            if (!this.getItemIdByUuid(uuid)) {
                unseenUuids.add(uuid);
            }
        }

        if (unseenUuids.size === 0) {
            return 0;
        }

        // Fetch asset data from API (will now include AI descriptions for source media)
        const assets = await this.fetchItemsFromApi(unseenUuids);

        // Add each asset to the registry
        let addedCount = 0;
        for (const asset of assets) {
            if (asset.background) {
                this.addItem(asset);
                addedCount++;
            }
        }

        return addedCount;
    }
}
